<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import AppointmentsAPI from 'dashboard/api/appointments';
import StatusBadge from 'dashboard/components-next/appointments/StatusBadge.vue';
import AppointmentBookingModal from 'dashboard/components-next/appointments/AppointmentBookingModal.vue';

const props = defineProps({
  pipelineCardId: {
    type: [Number, String],
    required: true,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['appointmentCreated']);

const { t, locale } = useI18n();
const store = useStore();

// "appointments" → vinculados a ESTE card via pipeline_card_id
// "contactHistory" → todos os agendamentos do contato (qualquer origem),
// usados na seção "Histórico do contato". Deduplicação por id evita
// repetir os do card na seção de histórico.
const appointments = ref([]);
const contactHistory = ref([]);
const loading = ref(false);
const showModal = ref(false);
const showAll = ref(false);

const upcomingAppointment = computed(() => {
  const now = new Date();
  return (
    appointments.value.find(
      a => new Date(a.scheduled_at) > now && a.status !== 'cancelled'
    ) || null
  );
});

const displayedAppointments = computed(() => {
  if (showAll.value) return appointments.value;
  return appointments.value.slice(0, 3);
});

// Histórico do contato deduplicado: remove tudo que já está em `appointments`
// (vinculado a este card). Mostra appointments criados em outros cards/conversas
// ou diretamente na agenda. Ordenado mais recente primeiro.
const otherContactAppointments = computed(() => {
  if (!contactHistory.value.length) return [];
  const cardIds = new Set(appointments.value.map(a => a.id));
  return contactHistory.value
    .filter(a => !cardIds.has(a.id))
    .slice()
    .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
});

async function loadCardAppointments() {
  try {
    const response = await AppointmentsAPI.list({
      pipeline_card_id: props.pipelineCardId,
    });
    const raw = response.data?.data || [];
    appointments.value = [...raw].sort(
      (a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)
    );
  } catch {
    appointments.value = [];
  }
}

async function loadContactHistory() {
  if (!props.contactId) {
    contactHistory.value = [];
    return;
  }
  try {
    const accountId = store.getters.getCurrentAccountId;
    const response = await AppointmentsAPI.contactHistory(
      accountId,
      props.contactId
    );
    contactHistory.value = response.data?.data || [];
  } catch {
    contactHistory.value = [];
  }
}

async function load() {
  loading.value = true;
  try {
    await Promise.all([loadCardAppointments(), loadContactHistory()]);
  } finally {
    loading.value = false;
  }
}

function formatDateTime(iso) {
  return new Intl.DateTimeFormat((locale.value || 'en-US').replace('_', '-'), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

function formatPrice(cents, currency = 'BRL') {
  if (cents == null) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

function handleCreated() {
  showModal.value = false;
  load();
  emit('appointmentCreated');
}

// Inline status actions on the card so agents don't have to leave to /appointments.
const TERMINAL_STATUSES = ['cancelled', 'completed', 'no_show'];
const actionLoadingId = ref(null);

const ACTION_CLASS = {
  confirm: 'text-n-teal-11 bg-n-teal-3/40 hover:bg-n-teal-3',
  complete: 'text-n-brand bg-n-brand/10 hover:bg-n-brand/20',
  cancel: 'text-n-ruby-11 bg-n-ruby-3/40 hover:bg-n-ruby-3',
};
const ACTION_LABEL_KEY = {
  confirm: 'APPOINTMENTS.PIPELINE_BLOCK.ACTIONS.CONFIRM',
  complete: 'APPOINTMENTS.PIPELINE_BLOCK.ACTIONS.COMPLETE',
  cancel: 'APPOINTMENTS.PIPELINE_BLOCK.ACTIONS.CANCEL',
};

function availableActions(status) {
  if (TERMINAL_STATUSES.includes(status)) return [];
  const actions = [];
  if (status === 'scheduled') actions.push('confirm');
  actions.push('complete', 'cancel');
  return actions;
}

async function runAction(appt, action) {
  actionLoadingId.value = appt.id;
  try {
    if (action === 'cancel') {
      await store.dispatch('appointmentsModule/cancel', { id: appt.id });
    } else {
      await store.dispatch(`appointmentsModule/${action}`, appt.id);
    }
    await load();
    emit('appointmentCreated');
  } finally {
    actionLoadingId.value = null;
  }
}

onMounted(load);
</script>

<template>
  <div class="border-t border-n-weak pt-4 mt-4">
    <!-- Header -->
    <div class="flex items-center justify-between mb-3 px-1">
      <h3
        class="text-[13px] font-bold text-n-slate-12 uppercase tracking-wide flex items-center gap-1.5"
      >
        <span
          class="i-lucide-calendar-clock size-4 text-n-slate-10"
          aria-hidden="true"
        />
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.TITLE') }}
      </h3>
      <button
        type="button"
        class="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-n-brand bg-n-brand/10 hover:bg-n-brand/20 px-2.5 py-1 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand/50"
        @click="showModal = true"
      >
        <span class="i-lucide-plus size-3" aria-hidden="true" />
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.ADD') }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="space-y-2 px-1">
      <div class="h-14 bg-n-alpha-1 rounded-xl animate-pulse" />
      <div class="h-10 bg-n-alpha-1 rounded-xl animate-pulse w-3/4" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="appointments.length === 0"
      class="rounded-xl border border-dashed border-n-strong/50 bg-n-alpha-1/50 hover:bg-n-alpha-1 transition-colors p-5 text-center flex flex-col items-center justify-center gap-2 cursor-pointer group"
      @click="showModal = true"
    >
      <span
        class="i-lucide-calendar-plus size-6 text-n-slate-9 group-hover:text-n-brand transition-colors duration-300"
        aria-hidden="true"
      />
      <p
        class="text-[13px] text-n-slate-11 font-medium group-hover:text-n-slate-12 transition-colors"
      >
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.EMPTY') }}
      </p>
    </div>

    <!-- Next upcoming highlight -->
    <div v-else-if="upcomingAppointment && !showAll" class="mb-3">
      <div
        class="rounded-xl border border-n-brand/20 bg-gradient-to-br from-n-brand/10 to-n-brand/5 p-3.5 shadow-sm ring-1 ring-n-brand/10 relative overflow-hidden group hover:shadow-md transition-all"
      >
        <!-- Shine effect on hover -->
        <div
          class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-[150%] group-hover:translate-x-[150%] transition-transform duration-1000 ease-out pointer-events-none"
        />

        <div class="flex flex-col gap-2 relative z-10">
          <div class="flex items-center justify-between gap-2">
            <StatusBadge :status="upcomingAppointment.status" />
            <span
              v-if="upcomingAppointment.price_cents"
              class="text-[11px] font-bold text-n-teal-11 bg-n-teal-3/50 px-2 py-0.5 rounded-md flex-shrink-0"
            >
              {{
                formatPrice(
                  upcomingAppointment.price_cents,
                  upcomingAppointment.currency
                )
              }}
            </span>
          </div>

          <div class="min-w-0">
            <p class="text-sm font-semibold text-n-slate-12 truncate">
              {{ upcomingAppointment.service?.name }}
            </p>
            <div
              class="flex flex-wrap items-center text-[12px] text-n-slate-11 mt-1 gap-x-2 gap-y-1"
            >
              <span class="flex items-center gap-1 font-medium text-n-slate-12">
                <span class="i-lucide-clock size-3" aria-hidden="true" />
                {{ formatDateTime(upcomingAppointment.scheduled_at) }}
              </span>
              <span
                v-if="upcomingAppointment.professional"
                class="flex items-center gap-1 truncate"
              >
                &middot;
                <span class="i-lucide-user size-3" aria-hidden="true" />
                <span class="truncate">{{
                  upcomingAppointment.professional.name
                }}</span>
              </span>
            </div>
          </div>

          <div
            v-if="availableActions(upcomingAppointment.status).length"
            class="flex items-center gap-1.5 pt-1"
          >
            <button
              v-for="action in availableActions(upcomingAppointment.status)"
              :key="action"
              type="button"
              :disabled="actionLoadingId === upcomingAppointment.id"
              class="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand/40 disabled:opacity-50"
              :class="ACTION_CLASS[action]"
              @click="runAction(upcomingAppointment, action)"
            >
              {{ t(ACTION_LABEL_KEY[action]) }}
            </button>
          </div>
        </div>
      </div>

      <div class="flex justify-center mt-2">
        <button
          v-if="appointments.length > 1"
          type="button"
          class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10 hover:text-n-brand transition-colors focus:outline-none flex items-center gap-1"
          @click="showAll = true"
        >
          {{ t('APPOINTMENTS.PIPELINE_BLOCK.VIEW_ALL') }}
          <span class="bg-n-alpha-2 text-n-slate-12 px-1.5 rounded-full">{{
            appointments.length
          }}</span>
        </button>
      </div>
    </div>

    <!-- Full list -->
    <div v-else-if="showAll || !upcomingAppointment" class="space-y-2">
      <div
        v-for="appt in displayedAppointments"
        :key="appt.id"
        class="rounded-xl border border-n-weak bg-n-background hover:bg-n-alpha-1 p-3 text-sm transition-all shadow-sm hover:shadow-md hover:border-n-strong group"
      >
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-2">
            <StatusBadge :status="appt.status" />
            <span
              v-if="appt.price_cents"
              class="text-[10px] font-bold text-n-slate-10 bg-n-alpha-2 px-2 py-0.5 rounded-md flex-shrink-0"
            >
              {{ formatPrice(appt.price_cents, appt.currency) }}
            </span>
          </div>

          <div class="min-w-0">
            <p class="font-semibold text-n-slate-12 truncate text-[13px]">
              {{ appt.service?.name }}
            </p>
            <div
              class="flex flex-wrap items-center text-[11px] text-n-slate-10 mt-1 gap-x-2 gap-y-1"
            >
              <span class="flex items-center gap-1">
                {{ formatDateTime(appt.scheduled_at) }}
              </span>
              <span
                v-if="appt.professional"
                class="flex items-center gap-1 truncate"
              >
                &middot; {{ appt.professional.name }}
              </span>
            </div>
            <p
              v-if="appt.cancellation_reason"
              class="text-[11px] text-n-ruby-11 mt-1.5 bg-n-ruby-2 border border-n-ruby-4 rounded p-1.5 line-clamp-2"
            >
              {{ appt.cancellation_reason }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="appointments.length > 3 && !showAll" class="text-center pt-1">
        <button
          type="button"
          class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10 hover:text-n-brand transition-colors focus:outline-none flex items-center justify-center gap-1 w-full"
          @click="showAll = true"
        >
          {{ t('APPOINTMENTS.PIPELINE_BLOCK.VIEW_ALL') }}
          <span class="bg-n-alpha-2 text-n-slate-12 px-1.5 rounded-full">{{
            appointments.length
          }}</span>
        </button>
      </div>
    </div>

    <!-- Histórico do contato (appointments criados fora deste card) -->
    <div
      v-if="otherContactAppointments.length"
      class="mt-5 pt-4 border-t border-dashed border-n-weak"
    >
      <h4
        class="text-[11px] font-bold text-n-slate-10 uppercase tracking-wider mb-2 flex items-center gap-1.5"
      >
        <span class="i-lucide-history size-3.5" aria-hidden="true" />
        {{ t('APPOINTMENTS.PIPELINE_BLOCK.CONTACT_HISTORY') }}
        <span
          class="bg-n-alpha-2 text-n-slate-11 px-1.5 rounded-full text-[10px]"
        >
          {{ otherContactAppointments.length }}
        </span>
      </h4>
      <div class="space-y-1.5">
        <div
          v-for="appt in otherContactAppointments"
          :key="`hist-${appt.id}`"
          class="rounded-lg border border-n-weak bg-n-alpha-1/40 p-2.5 text-xs hover:bg-n-alpha-1 transition-colors"
        >
          <div class="flex items-center justify-between gap-2 mb-1">
            <StatusBadge :status="appt.status" />
            <span
              class="text-[10px] text-n-slate-10 font-medium uppercase tracking-wide"
            >
              {{ t('APPOINTMENTS.PIPELINE_BLOCK.OTHER_SOURCE') }}
            </span>
          </div>
          <p class="font-semibold text-n-slate-12 truncate">
            {{ appt.service?.name }}
          </p>
          <div
            class="flex flex-wrap items-center text-[11px] text-n-slate-10 mt-0.5 gap-x-2"
          >
            <span>{{ formatDateTime(appt.scheduled_at) }}</span>
            <span v-if="appt.professional" class="truncate">
              &middot; {{ appt.professional.name }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Booking modal -->
    <AppointmentBookingModal
      :show="showModal"
      :prefill-contact-id="contactId"
      :pipeline-card-id="pipelineCardId"
      @close="showModal = false"
      @created="handleCreated"
    />
  </div>
</template>
